import {
  AdminPanelSettings,
  Close,
  Inventory2,
  Search,
  Sync,
  Tune,
} from "@mui/icons-material";
import { Dialog, Tab, Tabs } from "@mui/material";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import api, { baseURL } from "config/api";
import { useAppSelector } from "hooks/store";
import { ORDER, ORDER_NAME } from "utils/constants";
import { getProductOrderTypeAvailability } from "utils/orderTypeAvailability";
import { toast } from "react-toastify";

const MAX_IMAGE_FILE_SIZE = 2 * 1024 * 1024;
const OPENAI_IMAGE_MODEL = "gpt-image-1";
const OPENAI_API_KEY_STORAGE_KEY = "techAdminOpenAiApiKey";

const getConfiguredOpenAiApiKey = () => {
  try {
    const savedKey = window.localStorage.getItem(OPENAI_API_KEY_STORAGE_KEY);
    if (savedKey) return savedKey;
  } catch {
    // localStorage can be unavailable in private/browser-restricted contexts.
  }

  return (
    (window as any)?.config?.REACT_APP_OPENAI_API_KEY ||
    process.env.REACT_APP_OPENAI_API_KEY ||
    ""
  );
};

const conditionKeys = [
  {
    label: "pizzasInDeal",
    keys: ["pizzasInDeal", "PizzasInDeal"],
    description: "Number of pizza slots inside this deal.",
  },
  {
    label: "Create Own",
    keys: ["isCreateYourOwn", "IsCreateYourOwn"],
    description: "Uses create-your-own pizza flow and topping-price API.",
    type: "flag",
  },
  {
    label: "isDeal",
    keys: ["isDeal", "IsDeal"],
    description: "Treats product as a deal item in cart/order logic.",
  },
  {
    label: "isPizza",
    keys: ["isPizza", "IsPizza"],
    description: "Enables pizza size, crust, spice, and topping logic.",
  },
  {
    label: "CombinedTopping",
    keys: [
      "CombinedTopping",
      "combinedTopping",
      "totalCombinetoppings",
      "totalCombineToppings",
      "TotalCombinetoppings",
      "TotalCombineToppings",
    ],
    description: "Free topping count/limit shared for this product or deal.",
  },
  {
    label: "isServiceSub",
    keys: ["isServiceSub", "IsServiceSub"],
    description: "Hides special instructions, crusts, and half-selection topping options.",
  },
  {
    label: "toppingsEachPizza",
    keys: ["toppingsEachPizza", "ToppingsEachPizza"],
    description: "Default/free topping count used per pizza.",
  },
];

const getValue = (item: any, keys: string[]) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) return item[key];
  }

  return "";
};

const isTruthyFlag = (value: any) =>
  value === true || Number(value || 0) === 1 || String(value || "").toLowerCase() === "true";

const getCategoryId = (category: any) => Number(category?.id || category?.categoryId || 0);

const getCategoryName = (category: any) =>
  String(category?.categoryDescription || category?.name || category?.categoryName || "");

const getProductId = (product: any) => Number(product?.productId || product?.id || 0);

const getProductName = (product: any) =>
  String(product?.productName || product?.fullDescription || product?.name || "");

const hasImage = (item: any) => !!String(item?.imageUrl || "").trim();

const base64ToFile = (base64: string, fileName: string, mimeType = "image/png") => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], fileName, { type: mimeType });
};

const formatMoney = (value: any) => {
  const amount = Number(value || 0);
  return `$${amount.toFixed(2)}`;
};

const getPriceValue = (item: any) => {
  const keys = [
    "price",
    "sizePriceX1",
    "toppingPrice1",
    "modifierCost",
    "modifierPrice",
    "pizzaModifierPrice",
  ];

  for (const key of keys) {
    const value = Number(item?.[key]);
    if (!Number.isNaN(value) && value > 0) return value;
  }

  return 0;
};

const getPriceLabel = (item: any, index: number) =>
  String(
    item?.sizeName ||
      item?.modifierName ||
      item?.pizzaModifierName ||
      item?.name ||
      item?.description ||
      `Row ${index + 1}`,
  );

const getCreateYourOwnTierPrices = (item: any) =>
  Object.entries(item || {})
    .map(([key, value]: any) => {
      const match = /^toppingPrice(\d+)$/i.exec(key);
      return match && Number(value || 0) > 0
        ? {
            label: `${match[1]} topping${Number(match[1]) === 1 ? "" : "s"}`,
            value: Number(value),
          }
        : null;
    })
    .filter(Boolean) as { label: string; value: number }[];

const getOptionLevelName = (level: any, index: number) =>
  String(
    level?.levelName ||
      level?.nameLevel ||
      level?.name ||
      level?.title ||
      `Option Group ${index + 1}`,
  );

const getOptionItems = (level: any) =>
  Array.isArray(level?.data)
    ? level.data
    : Array.isArray(level?.items)
    ? level.items
    : Array.isArray(level?.options)
    ? level.options
    : [];

const getOptionItemName = (item: any, index: number) =>
  String(item?.name || item?.itemName || item?.description || `Option ${index + 1}`);

const getOptionItemPrice = (item: any) =>
  Number(item?.price ?? item?.itemPrice ?? item?.amount ?? 0);

const TechAdmin = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [products, setProducts] = useState<any[]>([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [product, setProduct] = useState<any>(null);
  const [apiCalls, setApiCalls] = useState<any[]>([]);
  const [responsePopup, setResponsePopup] = useState<any>(null);
  const [error, setError] = useState("");
  const [bulkTargetType, setBulkTargetType] = useState<"product" | "category">("product");
  const [bulkSelectedProductId, setBulkSelectedProductId] = useState("");
  const [bulkImageFile, setBulkImageFile] = useState<File | null>(null);
  const [bulkGeneratedFile, setBulkGeneratedFile] = useState<File | null>(null);
  const [bulkOnlyMissingImages, setBulkOnlyMissingImages] = useState(true);
  const [bulkProductSearch, setBulkProductSearch] = useState("");
  const [bulkSelectedProductIds, setBulkSelectedProductIds] = useState<number[]>([]);
  const [openAiApiKey, setOpenAiApiKey] = useState(() => getConfiguredOpenAiApiKey());
  const [imagePrompt, setImagePrompt] = useState("");
  const [imageBusy, setImageBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [bulkLog, setBulkLog] = useState<string[]>([]);
  const sideBarMenu = useAppSelector(({ main }) => main.sideBarMenu);

  useEffect(() => {
    if (sideBarMenu?.length) {
      setCategories(sideBarMenu);
      return;
    }

    let cancelled = false;
    api
      .get("/api/initialData")
      .then(({ data }) => {
        if (!cancelled) setCategories(data?.categories || []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });

    return () => {
      cancelled = true;
    };
  }, [sideBarMenu]);

  useEffect(() => {
    if (!selectedCategoryId && categories.length) {
      setSelectedCategoryId(String(getCategoryId(categories[0])));
    }
  }, [categories, selectedCategoryId]);

  useEffect(() => {
    if (!selectedCategoryId) return;

    let cancelled = false;
    const category = categories.find(
      (item) => getCategoryId(item) === Number(selectedCategoryId),
    );
    const params = new URLSearchParams();
    params.set("categoryId", selectedCategoryId);
    params.set("showAll", "true");

    setProductsLoading(true);
    setProducts([]);
    setSelectedProductId("");
    setProduct(null);
    setApiCalls([]);
    setError("");

    api
      .get("/api/Product/ByCategoryId?" + params.toString())
      .then(({ data }) => {
        if (cancelled) return;
        setProducts(
          (data || []).map((item: any) => ({
            ...item,
            __categoryId: selectedCategoryId,
            __categoryName: getCategoryName(category),
          })),
        );
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Unable to load products for category.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [categories, selectedCategoryId]);

  const conditionRows = useMemo(
    () =>
      conditionKeys.map((item) => ({
        label: item.label,
        description: item.description,
        value: product
          ? (item as any).type === "flag"
            ? isTruthyFlag(getValue(product, item.keys))
              ? "Yes"
              : "No"
            : getValue(product, item.keys)
          : "",
      })),
    [product],
  );

  const orderTypeRows = useMemo(
    () =>
      [ORDER.TAKE_OUT, ORDER.DELIVERY].map((orderType) => {
        const availability = product
          ? getProductOrderTypeAvailability(product, orderType)
          : { unavailable: false, reason: "" };

        return {
          orderType,
          label: ORDER_NAME[orderType] || orderType,
          ...availability,
        };
      }),
    [product],
  );

  const sourceApiCalls = useMemo(() => {
    const calls = [
      {
        title: "Category List",
        reason: "Loads admin category dropdown",
        url: "/api/initialData",
        fullUrl: `${baseURL}/api/initialData`,
        data: categories,
      },
    ];

    if (selectedCategoryId) {
      const url = `/api/Product/ByCategoryId?categoryId=${selectedCategoryId}&showAll=true`;
      calls.push({
        title: "Product List",
        reason: "Loads product dropdown for selected category",
        url,
        fullUrl: `${baseURL}${url}`,
        data: products,
      });
    }

    return calls;
  }, [categories, products, selectedCategoryId]);

  const loadConditions = async (selectedItem?: any) => {
    const foundProduct =
      selectedItem ||
      products.find(
        (item: any) => Number(item?.productId || item?.id) === Number(selectedProductId),
      );
    const id = Number(foundProduct?.productId || foundProduct?.id || 0);
    if (!id) {
      setError("Select product");
      return;
    }

    setLoading(true);
    setApiCalls([]);
    setError("");

    try {
      const calls: any[] = [];
      const isPizza = Number(getValue(foundProduct, ["isPizza", "IsPizza"]) || 0) === 1;
      const isCreateYourOwn =
        Number(getValue(foundProduct, ["isCreateYourOwn", "IsCreateYourOwn"]) || 0) ===
        1;

      if (isPizza && isCreateYourOwn) {
        const url = `/api/PizzaToppingPrice?productId=${id}`;
        const { data } = await api.get(url);
        calls.push({
          title: "Create Your Own Topping Price",
          reason: "isPizza = 1 and isCreateYourOwn = 1",
          url,
          fullUrl: `${baseURL}${url}`,
          data: data || [],
        });
      }

      if (isPizza && !isCreateYourOwn) {
        const url = `/api/PizzaSize/ByProductId?productId=${id}`;
        const { data } = await api.get(url);
        calls.push({
          title: "Pizza Size Price",
          reason: "isPizza = 1 and isCreateYourOwn = 0",
          url,
          fullUrl: `${baseURL}${url}`,
          data: data || [],
        });
      }

      const optionsUrl = `/api/Variation/VariantById?productId=${id}`;
      const { data: optionsData } = await api.get(optionsUrl);
      calls.push({
        title: "Available Options",
        reason: "Loads option groups, mandatory selection count, max count, and prices",
        url: optionsUrl,
        fullUrl: `${baseURL}${optionsUrl}`,
        data: Array.isArray(optionsData) ? optionsData : [],
        type: "options",
        optionData: Array.isArray(optionsData) ? optionsData : [],
      });

      setProduct(foundProduct);
      setApiCalls(calls);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load condition details.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (value: string) => {
    setSelectedProductId(value);
    const selectedItem = products.find(
      (item: any) => Number(item?.productId || item?.id) === Number(value),
    );
    if (selectedItem) loadConditions(selectedItem);
  };

  const selectedCategory = useMemo(
    () => categories.find((item) => getCategoryId(item) === Number(selectedCategoryId)),
    [categories, selectedCategoryId],
  );

  const bulkSelectedProduct = useMemo(
    () => products.find((item) => getProductId(item) === Number(bulkSelectedProductId)),
    [bulkSelectedProductId, products],
  );

  const bulkVisibleProducts = useMemo(
    () => {
      const searchText = bulkProductSearch.trim().toLowerCase();

      return products.filter((item) => {
        const matchesImageFilter = !bulkOnlyMissingImages || !hasImage(item);
        const matchesSearch =
          !searchText ||
          getProductName(item).toLowerCase().includes(searchText) ||
          String(getProductId(item)).includes(searchText);

        return matchesImageFilter && matchesSearch;
      });
    },
    [bulkOnlyMissingImages, bulkProductSearch, products],
  );

  const bulkImagePreview = useMemo(
    () => (bulkImageFile ? URL.createObjectURL(bulkImageFile) : ""),
    [bulkImageFile],
  );

  const bulkGeneratedPreview = useMemo(
    () => (bulkGeneratedFile ? URL.createObjectURL(bulkGeneratedFile) : ""),
    [bulkGeneratedFile],
  );

  useEffect(() => {
    return () => {
      if (bulkImagePreview) URL.revokeObjectURL(bulkImagePreview);
    };
  }, [bulkImagePreview]);

  useEffect(() => {
    return () => {
      if (bulkGeneratedPreview) URL.revokeObjectURL(bulkGeneratedPreview);
    };
  }, [bulkGeneratedPreview]);

  useEffect(() => {
    setBulkSelectedProductId("");
    setBulkSelectedProductIds([]);
  }, [selectedCategoryId]);

  useEffect(() => {
    try {
      if (openAiApiKey.trim()) {
        window.localStorage.setItem(OPENAI_API_KEY_STORAGE_KEY, openAiApiKey.trim());
      } else {
        window.localStorage.removeItem(OPENAI_API_KEY_STORAGE_KEY);
      }
    } catch {
      // Ignore storage failures; the key can still be used for this session.
    }
  }, [openAiApiKey]);

  const getBulkTarget = () => {
    if (bulkTargetType === "category") {
      return {
        id: getCategoryId(selectedCategory),
        name: getCategoryName(selectedCategory),
        item: selectedCategory,
        type: "category" as const,
      };
    }

    return {
      id: getProductId(bulkSelectedProduct),
      name: getProductName(bulkSelectedProduct),
      item: bulkSelectedProduct,
      type: "product" as const,
    };
  };

  const buildImagePrompt = (target: { name: string; type: "product" | "category" }) =>
    imagePrompt.trim() ||
    `Create a clean square food ordering image for the ${target.type} "${target.name}". Use appetizing realistic restaurant photography, isolated subject, bright natural lighting, no text, no logos, no watermark.`;

  const refreshBulkProducts = async () => {
    if (!selectedCategoryId) return;

    const category = categories.find(
      (item) => getCategoryId(item) === Number(selectedCategoryId),
    );
    const params = new URLSearchParams();
    params.set("categoryId", selectedCategoryId);
    params.set("showAll", "true");

    setProductsLoading(true);
    try {
      const { data } = await api.get("/api/Product/ByCategoryId?" + params.toString());
      setProducts(
        (data || []).map((item: any) => ({
          ...item,
          __categoryId: selectedCategoryId,
          __categoryName: getCategoryName(category),
        })),
      );
    } finally {
      setProductsLoading(false);
    }
  };

  const uploadImageFile = async (
    targetType: "product" | "category",
    id: number,
    file: File,
  ) => {
    const formData = new FormData();
    formData.append("formFile", file);

    const url =
      targetType === "category"
        ? `/api/FileUtility/UploadDeptFiles?deptid=${id}`
        : `/api/FileUtility/UploadFile?itemid=${id}`;
    const response = await api.post(url, formData);
    const responseData = response.data?.data?.[0];

    if (responseData?.status === "ERROR") {
      throw new Error(responseData.errorMessage || "Failed to upload the image.");
    }
  };

  const handleBulkFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";

    if (file && file.size > MAX_IMAGE_FILE_SIZE) {
      toast.error("File size must not exceed 2 MB.");
      return;
    }

    setBulkImageFile(file);
  };

  const handleUploadSelectedImage = async (file: File | null) => {
    const target = getBulkTarget();

    if (!target.id) {
      toast.error(`Select a ${bulkTargetType} first.`);
      return;
    }

    if (!file) {
      toast.error("Select or generate an image first.");
      return;
    }

    setUploadBusy(true);
    try {
      await uploadImageFile(target.type, target.id, file);
      toast.success(`${target.name || "Image"} uploaded successfully`);
      setBulkLog((prev) => [`Uploaded ${target.type}: ${target.name}`, ...prev].slice(0, 12));
      if (target.type === "product") await refreshBulkProducts();
      if (target.type === "category") {
        const { data } = await api.get("/api/initialData");
        setCategories(data?.categories || []);
      }
    } catch (err: any) {
      toast.error(err?.message || "Unable to upload image");
    } finally {
      setUploadBusy(false);
    }
  };

  const generateImageFile = async (target: { name: string; type: "product" | "category" }) => {
    if (!openAiApiKey.trim()) {
      throw new Error("Enter OpenAI API key first.");
    }
    if (!target.name.trim()) {
      throw new Error(`Select a ${target.type} first.`);
    }

    const response = await fetch("https://api.openai.com/v1/images/generations", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${openAiApiKey.trim()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: OPENAI_IMAGE_MODEL,
        prompt: buildImagePrompt(target),
        size: "1024x1024",
        n: 1,
      }),
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error?.message || "OpenAI image generation failed.");
    }

    const base64 = data?.data?.[0]?.b64_json;
    if (!base64) {
      throw new Error("OpenAI did not return image data.");
    }

    return base64ToFile(
      base64,
      `${target.type}-${target.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "image"}.png`,
    );
  };

  const handleGenerateSelectedImage = async () => {
    const target = getBulkTarget();

    setImageBusy(true);
    try {
      const file = await generateImageFile(target);
      setBulkGeneratedFile(file);
      toast.success("Image generated");
      setBulkLog((prev) => [`Generated ${target.type}: ${target.name}`, ...prev].slice(0, 12));
    } catch (err: any) {
      toast.error(err?.message || "Unable to generate image");
    } finally {
      setImageBusy(false);
    }
  };

  const toggleBulkProduct = (productId: number) => {
    setBulkSelectedProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId],
    );
  };

  const handleBulkGenerateProducts = async () => {
    const selectedProducts = products.filter((item) =>
      bulkSelectedProductIds.includes(getProductId(item)),
    );

    if (!selectedProducts.length) {
      toast.error("Select products for bulk generation.");
      return;
    }

    setImageBusy(true);
    setBulkLog((prev) => [`Starting bulk generation for ${selectedProducts.length} products`, ...prev].slice(0, 12));
    try {
      for (const item of selectedProducts) {
        const target = {
          id: getProductId(item),
          name: getProductName(item),
          type: "product" as const,
        };
        const file = await generateImageFile(target);
        await uploadImageFile("product", target.id, file);
        setBulkLog((prev) => [`Generated and uploaded: ${target.name}`, ...prev].slice(0, 12));
      }

      toast.success("Bulk image generation completed");
      setBulkSelectedProductIds([]);
      await refreshBulkProducts();
    } catch (err: any) {
      toast.error(err?.message || "Bulk image generation stopped");
    } finally {
      setImageBusy(false);
    }
  };

  const handleGenerateSingleProduct = async (item: any) => {
    const target = {
      id: getProductId(item),
      name: getProductName(item),
      type: "product" as const,
    };

    if (!target.id) {
      toast.error("Product id is missing.");
      return;
    }

    setImageBusy(true);
    try {
      const file = await generateImageFile(target);
      await uploadImageFile("product", target.id, file);
      toast.success(`${target.name || "Product"} image uploaded`);
      setBulkLog((prev) => [`Generated and uploaded: ${target.name}`, ...prev].slice(0, 12));
      await refreshBulkProducts();
    } catch (err: any) {
      toast.error(err?.message || "Unable to generate product image");
    } finally {
      setImageBusy(false);
    }
  };

  return (
    <div
      className="min-h-full bg-gray-50 px-3 py-3 text-gray-950 sm:px-4"
      style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
    >
      <div className="flex w-full flex-col gap-3">
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-black text-white">
                <AdminPanelSettings className="!text-[24px]" />
              </span>
              <div>
                <h1 className="text-xl font-bold text-gray-950">Tech Admin</h1>
                <p className="text-sm font-normal text-gray-600">
                  Read-only condition and pricing diagnostics.
                </p>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700">
              API: {baseURL}
            </div>
          </div>

          <div className="mt-3 border-t border-gray-100 pt-1">
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              sx={{
                minHeight: 48,
                "& .MuiTab-root": {
                  color: "#4b5563",
                  fontWeight: 800,
                  minHeight: 48,
                  textTransform: "none",
                },
                "& .Mui-selected": {
                  color: "#111827 !important",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#111827",
                  height: 3,
                },
                "& .Mui-disabled": {
                  color: "#9ca3af !important",
                },
              }}
            >
              <Tab label="Conditions" />
              <Tab label="Bulk Image" />
            </Tabs>
          </div>
        </div>

        {activeTab === 0 && (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-[340px_minmax(0,1fr)] 2xl:grid-cols-[360px_minmax(0,1fr)]">
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-base font-bold text-gray-950">
                <Tune className="!text-[20px]" />
                Conditions Check
              </div>
              <p className="mt-1 text-sm font-normal text-gray-600">
                Select a category and product to check which condition API is used.
              </p>

              <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-gray-500">
                Category
              </label>
              <select
                value={selectedCategoryId}
                onChange={(event) => setSelectedCategoryId(event.target.value)}
                className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-gray-900"
              >
                {!categories.length && <option value="">Loading categories...</option>}
                {categories.map((category) => (
                  <option key={getCategoryId(category)} value={getCategoryId(category)}>
                    {getCategoryName(category)}
                  </option>
                ))}
              </select>

              <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-gray-500">
                Product
              </label>
              <select
                value={selectedProductId}
                onChange={(event) => handleProductSelect(event.target.value)}
                disabled={!selectedCategoryId || productsLoading}
                className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-gray-900 disabled:bg-gray-100 disabled:text-gray-400"
              >
                <option value="">
                  {productsLoading ? "Loading products..." : "Select product"}
                </option>
                {products.map((item: any) => (
                  <option key={item.productId || item.id} value={item.productId || item.id}>
                    {item.productName || item.fullDescription || `Product ${item.productId || item.id}`}
                  </option>
                ))}
              </select>

              <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                <div className="flex min-w-0 items-center gap-2 text-xs font-semibold text-gray-500">
                  <Inventory2 className="!text-[18px]" />
                  <span>{products.length} product{products.length === 1 ? "" : "s"} loaded</span>
                </div>
                <button
                  type="button"
                  onClick={() => loadConditions()}
                  disabled={loading || !selectedProductId}
                  className="flex items-center gap-2 rounded-lg bg-black px-3 py-2 text-xs font-bold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {loading ? (
                    <Sync className="!text-[18px] animate-spin" />
                  ) : (
                    <Search className="!text-[18px]" />
                  )}
                  Check
                </button>
              </div>

              {!!error && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-600">
                  {error}
                </div>
              )}

              <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 p-3 text-xs leading-5 text-gray-500">
                Example: if product has <b>isPizza = 1</b> and{" "}
                <b>isCreateYourOwn = 1</b>, this page calls{" "}
                <b>/api/PizzaToppingPrice</b>.
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-3">
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-950">
                      Product Conditions
                    </h2>
                    <p className="text-sm font-normal text-gray-600">
                      {product
                        ? `${product.productName || product.fullDescription || "Product"} ${
                            product.__categoryName ? `- ${product.__categoryName}` : ""
                          }`
                        : "No product loaded yet."}
                    </p>
                  </div>
                  {product && (
                    <div className="font-mono text-xs font-bold text-gray-700">
                      productId: {product.productId || product.id}
                    </div>
                  )}
                </div>

                <div className="mt-4 rounded-lg border border-gray-200 bg-white">
                  <div className="border-b border-gray-100 px-4 py-3">
                    <div className="text-sm font-bold text-gray-950">
                      Category and Product APIs
                    </div>
                    <div className="mt-1 text-xs font-semibold text-gray-600">
                      Source calls used to load the dropdowns.
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {sourceApiCalls.map((call) => (
                      <div
                        key={call.url}
                            className="grid gap-2 px-4 py-3 lg:grid-cols-[160px_minmax(0,1fr)_auto] lg:items-center"
                      >
                        <div className="min-w-0 lg:col-span-2">
                          <div className="text-sm font-bold text-gray-950">
                            {call.title}
                          </div>
                          <div className="mt-1 text-xs font-semibold text-gray-600">
                            {call.reason}
                          </div>
                          <div className="mt-2 break-all rounded bg-gray-50 px-2 py-1 font-mono text-xs font-semibold text-gray-800">
                            {call.fullUrl}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setResponsePopup(call)}
                          className="inline-flex shrink-0 items-center justify-center rounded-lg bg-black px-3 py-2 text-xs font-bold text-white transition hover:bg-gray-800"
                        >
                          Check Response
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-gray-200 bg-white">
                  <div className="border-b border-gray-100 px-4 py-3">
                    <div className="text-sm font-bold text-gray-950">
                      Order Type Availability
                    </div>
                    <div className="mt-1 text-xs font-semibold text-gray-600">
                      Shows blocked order types from product disable order-type fields.
                    </div>
                  </div>
                  {product ? (
                    <div className="grid grid-cols-1 gap-2 p-4 lg:grid-cols-2">
                      {orderTypeRows.map((row) => (
                        <div
                          key={row.orderType}
                          className={`rounded-lg border px-3 py-3 ${
                            row.unavailable
                              ? "border-red-200 bg-red-50 text-red-700"
                              : "border-green-200 bg-green-50 text-green-700"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-sm font-bold">{row.label}</div>
                            <div
                              className={`rounded-full px-2 py-1 text-[11px] font-black uppercase ${
                                row.unavailable
                                  ? "bg-red-600 text-white"
                                  : "bg-green-600 text-white"
                              }`}
                            >
                              {row.unavailable ? "Not Allowed" : "Allowed"}
                            </div>
                          </div>
                          <div className="mt-2 text-xs font-semibold leading-5">
                            {row.unavailable
                              ? row.reason || `Not available for ${row.label}`
                              : `Available for ${row.label}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-5 text-sm font-semibold text-gray-500">
                      Select a product to check order type restrictions.
                    </div>
                  )}
                  {product && (
                    <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 text-xs leading-5 text-gray-600">
                      <span className="font-bold text-gray-700">Raw fields:</span>{" "}
                      disableonordertype:{" "}
                      {String(product?.disableonordertype ?? product?.DisableOnOrderType ?? "-")}{" "}
                      | disableordtype:{" "}
                      {String(product?.disableordtype ?? product?.DisableOrdType ?? "-")}
                    </div>
                  )}
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {conditionRows.map((row) => {
                    const numericValue = Number(row.value || 0);
                    const isActiveValue =
                      row.value !== "" &&
                      ((!Number.isNaN(numericValue) && numericValue !== 0) ||
                        String(row.value).toLowerCase() === "yes");

                    return (
                      <div
                        key={row.label}
                        className={`relative min-h-[88px] rounded-lg border px-3 py-2 pr-14 ${
                          isActiveValue
                            ? "border-amber-200 bg-amber-50/80"
                            : "border-gray-100 bg-gray-50"
                        }`}
                      >
                        <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
                          {row.label}
                        </div>
                        <div
                          className={`absolute right-3 top-3 flex h-8 min-w-8 items-center justify-center rounded-lg border px-2 font-mono text-sm font-black ${
                            isActiveValue
                              ? "condition-value-pulse border-amber-300 bg-amber-100 text-amber-900"
                              : "border-gray-200 bg-white text-gray-800"
                          }`}
                        >
                          {String(row.value === "" ? "-" : row.value)}
                        </div>
                        <div className="mt-3 text-[11px] font-normal leading-4 text-gray-700">
                          {row.description}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {product && (
                  <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
                    <div className="text-sm font-bold text-blue-950">
                      What these conditions do
                    </div>
                    <div className="mt-2 space-y-1 text-sm leading-6 text-blue-900">
                      {Number(getValue(product, ["CombinedTopping", "combinedTopping", "totalCombinetoppings", "totalCombineToppings", "TotalCombinetoppings", "TotalCombineToppings"]) || 0) > 0 && (
                        <p>
                          <b>CombinedTopping</b> gives{" "}
                          {getValue(product, [
                            "CombinedTopping",
                            "combinedTopping",
                            "totalCombinetoppings",
                            "totalCombineToppings",
                            "TotalCombinetoppings",
                            "TotalCombineToppings",
                          ])}{" "}
                          free topping
                          {Number(
                            getValue(product, [
                              "CombinedTopping",
                              "combinedTopping",
                              "totalCombinetoppings",
                              "totalCombineToppings",
                              "TotalCombinetoppings",
                              "TotalCombineToppings",
                            ]),
                          ) === 1
                            ? ""
                            : "s"}{" "}
                          before extra topping price is added.
                        </p>
                      )}
                      {Number(getValue(product, ["isServiceSub", "IsServiceSub"]) || 0) === 1 && (
                        <p>
                          <b>isServiceSub</b> hides crust, special instructions, and half
                          topping buttons. The 2x topping button stays available.
                        </p>
                      )}
                      {Number(getValue(product, ["pizzasInDeal", "PizzasInDeal"]) || 0) > 0 && (
                        <p>
                          <b>pizzasInDeal</b> creates{" "}
                          {getValue(product, ["pizzasInDeal", "PizzasInDeal"])} pizza slot
                          {Number(getValue(product, ["pizzasInDeal", "PizzasInDeal"])) === 1
                            ? ""
                            : "s"}{" "}
                          for selection/customization.
                        </p>
                      )}
                      {Number(getValue(product, ["toppingsEachPizza", "ToppingsEachPizza"]) || 0) > 0 && (
                        <p>
                          <b>toppingsEachPizza</b> shows{" "}
                          {getValue(product, ["toppingsEachPizza", "ToppingsEachPizza"])} free
                          topping
                          {Number(getValue(product, ["toppingsEachPizza", "ToppingsEachPizza"])) === 1
                            ? ""
                            : "s"}{" "}
                          per pizza.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <h2 className="text-lg font-bold text-gray-950">
                  Condition API Results
                </h2>
                <p className="mt-1 text-sm font-normal text-gray-600">
                  Pricing data returned from the condition-selected API calls.
                </p>

                {!apiCalls.length && (
                  <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 px-4 py-6 text-center text-sm font-semibold text-gray-500">
                    No pricing API called yet.
                  </div>
                )}

                <div className="mt-4 flex flex-col gap-3">
                  {apiCalls.map((call) => (
                    <div
                      key={call.url}
                      className="rounded-lg border border-gray-200 bg-white"
                    >
                      <div className="border-b border-gray-100 px-4 py-3">
                        <div className="font-bold text-gray-950">{call.title}</div>
                        <div className="mt-1 text-xs font-semibold text-gray-600">
                          {call.reason}
                        </div>
                        <div className="mt-2 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                          <div className="min-w-0 break-all rounded bg-gray-50 px-2 py-1 font-mono text-xs font-semibold text-gray-800">
                            {call.fullUrl || `${baseURL}${call.url}`}
                          </div>
                          <button
                            type="button"
                            onClick={() => setResponsePopup(call)}
                            className="inline-flex shrink-0 items-center justify-center rounded-lg bg-black px-3 py-2 text-xs font-bold text-white transition hover:bg-gray-800"
                          >
                            Check Response
                          </button>
                        </div>
                      </div>

                      {call.type === "options" ? (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[720px] text-left text-sm">
                            <thead>
                              <tr className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                                <th className="px-4 py-2">Option Group</th>
                                <th className="px-4 py-2">Mandatory</th>
                                <th className="px-4 py-2">Select Count</th>
                                <th className="px-4 py-2">Options and Prices</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(call.optionData || []).length ? (
                                (call.optionData || []).map((level: any, index: number) => {
                                  const items = getOptionItems(level);
                                  return (
                                    <tr
                                      key={`${call.url}-${index}`}
                                      className="border-b border-gray-100 last:border-b-0"
                                    >
                                      <td className="px-4 py-3 font-semibold text-gray-950">
                                        {getOptionLevelName(level, index)}
                                      </td>
                                      <td className="px-4 py-3">
                                        <span
                                          className={`rounded-full px-2 py-1 text-xs font-black ${
                                            Number(level?.mandatoryLevel || 0) > 0
                                              ? "bg-red-50 text-red-600"
                                              : "bg-gray-100 text-gray-600"
                                          }`}
                                        >
                                          {Number(level?.mandatoryLevel || 0) > 0
                                            ? `Minimum ${level.mandatoryLevel}`
                                            : "Optional"}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 font-mono font-bold text-gray-950">
                                        {Number(level?.qtyLevel || 0) > 0
                                          ? `Max ${level.qtyLevel}`
                                          : "No max"}
                                      </td>
                                      <td className="px-4 py-3 text-xs font-normal leading-5 text-gray-700">
                                        {items.length
                                          ? items
                                              .map((item: any, itemIndex: number) => {
                                                const price = getOptionItemPrice(item);
                                                return `${getOptionItemName(
                                                  item,
                                                  itemIndex,
                                                )}: ${formatMoney(price)}`;
                                              })
                                              .join(" | ")
                                          : "No options returned"}
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td
                                    colSpan={4}
                                    className="px-4 py-6 text-center text-sm font-semibold text-gray-500"
                                  >
                                    No options returned for this product.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[520px] text-left text-sm">
                            <thead>
                              <tr className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                                <th className="px-4 py-2">Name</th>
                                <th className="px-4 py-2">Price</th>
                                <th className="px-4 py-2">Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(Array.isArray(call.data) ? call.data : [call.data]).map(
                                (item: any, index: number) => {
                                  const tiers = getCreateYourOwnTierPrices(item);
                                  return (
                                    <tr
                                      key={`${call.url}-${index}`}
                                      className="border-b border-gray-100 last:border-b-0"
                                    >
                                      <td className="px-4 py-3 font-semibold text-gray-950">
                                        {getPriceLabel(item, index)}
                                      </td>
                                      <td className="px-4 py-3 font-mono font-bold text-gray-950">
                                        {tiers.length
                                          ? tiers.map((tier) => `${tier.label}: ${formatMoney(tier.value)}`).join(", ")
                                          : formatMoney(getPriceValue(item))}
                                      </td>
                                      <td className="px-4 py-3 text-xs font-normal leading-5 text-gray-700">
                                        {Object.entries(item || {})
                                          .slice(0, 8)
                                          .map(([key, value]) => `${key}: ${String(value)}`)
                                          .join(" | ")}
                                      </td>
                                    </tr>
                                  );
                                },
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 1 && (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-[360px_minmax(0,1fr)]">
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-base font-bold text-gray-950">Bulk Image</div>
              <p className="mt-1 text-sm font-normal text-gray-600">
                Upload or generate images for categories and products.
              </p>

              <div className="mt-4 grid grid-cols-2 gap-2 rounded-lg border border-gray-200 bg-gray-50 p-1">
                {(["product", "category"] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setBulkTargetType(type)}
                    className={`rounded-md px-3 py-2 text-xs font-black uppercase transition ${
                      bulkTargetType === type
                        ? "bg-black text-white"
                        : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>

              <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-gray-500">
                Category
              </label>
              <select
                value={selectedCategoryId}
                onChange={(event) => setSelectedCategoryId(event.target.value)}
                className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-gray-900"
              >
                {!categories.length && <option value="">Loading categories...</option>}
                {categories.map((category) => (
                  <option key={getCategoryId(category)} value={getCategoryId(category)}>
                    {getCategoryName(category)}
                  </option>
                ))}
              </select>

              {bulkTargetType === "product" && (
                <>
                  <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-gray-500">
                    Product
                  </label>
                  <select
                    value={bulkSelectedProductId}
                    onChange={(event) => setBulkSelectedProductId(event.target.value)}
                    disabled={!selectedCategoryId || productsLoading}
                    className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-gray-900 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <option value="">
                      {productsLoading ? "Loading products..." : "Select product"}
                    </option>
                    {products.map((item: any) => (
                      <option key={getProductId(item)} value={getProductId(item)}>
                        {getProductName(item) || `Product ${getProductId(item)}`}
                      </option>
                    ))}
                  </select>
                </>
              )}

              <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-gray-500">
                Manual Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleBulkFileChange}
                className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold"
              />
              {bulkImagePreview && (
                <img
                  src={bulkImagePreview}
                  alt=""
                  className="mt-3 h-40 w-full rounded-lg border border-gray-200 object-cover"
                />
              )}
              <button
                type="button"
                onClick={() => handleUploadSelectedImage(bulkImageFile)}
                disabled={uploadBusy || !bulkImageFile}
                className="mt-3 w-full rounded-lg bg-black px-3 py-2 text-sm font-bold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {uploadBusy ? "Uploading..." : "Upload Selected Image"}
              </button>

              <div className="mt-5 border-t border-gray-100 pt-4">
                <label className="block text-xs font-bold uppercase tracking-wide text-gray-500">
                  OpenAI API Key
                </label>
                <input
                  type="password"
                  value={openAiApiKey}
                  onChange={(event) => setOpenAiApiKey(event.target.value)}
                  placeholder="Paste key once or set REACT_APP_OPENAI_API_KEY"
                  className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-gray-900"
                />
                <div className="mt-2 flex items-center justify-between gap-2 text-xs font-semibold">
                  <span className={openAiApiKey.trim() ? "text-green-700" : "text-red-600"}>
                    {openAiApiKey.trim()
                      ? "OpenAI key loaded for image generation."
                      : "OpenAI key is not configured yet."}
                  </span>
                  {!!openAiApiKey.trim() && (
                    <button
                      type="button"
                      onClick={() => setOpenAiApiKey("")}
                      className="rounded border border-gray-200 bg-white px-2 py-1 font-bold text-gray-700 hover:bg-gray-50"
                    >
                      Clear
                    </button>
                  )}
                </div>

                <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-gray-500">
                  Prompt
                </label>
                <textarea
                  value={imagePrompt}
                  onChange={(event) => setImagePrompt(event.target.value)}
                  rows={4}
                  placeholder="Optional. Blank uses product/category name."
                  className="mt-2 w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-gray-900"
                />
                <button
                  type="button"
                  onClick={handleGenerateSelectedImage}
                  disabled={imageBusy}
                  className="mt-3 w-full rounded-lg bg-blue-700 px-3 py-2 text-sm font-bold text-white transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {imageBusy ? "Generating..." : "Generate Image"}
                </button>
                {bulkGeneratedPreview && (
                  <>
                    <img
                      src={bulkGeneratedPreview}
                      alt=""
                      className="mt-3 h-40 w-full rounded-lg border border-gray-200 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleUploadSelectedImage(bulkGeneratedFile)}
                      disabled={uploadBusy || !bulkGeneratedFile}
                      className="mt-3 w-full rounded-lg bg-emerald-700 px-3 py-2 text-sm font-bold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-gray-400"
                    >
                      Upload Generated Image
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-3">
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-950">
                      Product Image Queue
                    </h2>
                    <p className="text-sm font-normal text-gray-600">
                      Select products, then generate and upload images in sequence.
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={bulkOnlyMissingImages}
                      onChange={(event) => setBulkOnlyMissingImages(event.target.checked)}
                      className="h-4 w-4"
                    />
                    Missing images only
                  </label>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <input
                    value={bulkProductSearch}
                    onChange={(event) => setBulkProductSearch(event.target.value)}
                    placeholder="Search product name or ID"
                    className="min-w-[240px] flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-gray-900"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setBulkSelectedProductIds(
                        bulkVisibleProducts.map((item) => getProductId(item)).filter(Boolean),
                      )
                    }
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-gray-800 hover:bg-gray-50"
                  >
                    Select Visible
                  </button>
                  <button
                    type="button"
                    onClick={() => setBulkSelectedProductIds([])}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-gray-800 hover:bg-gray-50"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkGenerateProducts}
                    disabled={imageBusy || !bulkSelectedProductIds.length}
                    className="rounded-lg bg-black px-3 py-2 text-xs font-bold text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-400"
                  >
                    {imageBusy
                      ? "Generating..."
                      : `Generate Selected (${bulkSelectedProductIds.length})`}
                  </button>
                </div>

                <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full min-w-[720px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                        <th className="px-3 py-2">Use</th>
                        <th className="px-3 py-2">Product</th>
                        <th className="px-3 py-2">ID</th>
                        <th className="px-3 py-2">Image</th>
                        <th className="px-3 py-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkVisibleProducts.length ? (
                        bulkVisibleProducts.map((item) => {
                          const id = getProductId(item);
                          return (
                            <tr key={id} className="border-b border-gray-100 last:border-b-0">
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  checked={bulkSelectedProductIds.includes(id)}
                                  onChange={() => toggleBulkProduct(id)}
                                  className="h-4 w-4"
                                />
                              </td>
                              <td className="px-3 py-2 font-semibold text-gray-950">
                                {getProductName(item) || "Unnamed product"}
                              </td>
                              <td className="px-3 py-2 font-mono text-xs font-bold text-gray-700">
                                {id}
                              </td>
                              <td className="px-3 py-2">
                                {hasImage(item) ? (
                                  <span className="rounded-full bg-green-50 px-2 py-1 text-xs font-black text-green-700">
                                    Has Image
                                  </span>
                                ) : (
                                  <span className="rounded-full bg-red-50 px-2 py-1 text-xs font-black text-red-700">
                                    Missing
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <button
                                  type="button"
                                  onClick={() => handleGenerateSingleProduct(item)}
                                  disabled={imageBusy}
                                  className="rounded-lg bg-blue-700 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-gray-400"
                                >
                                  Generate
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={5} className="px-3 py-8 text-center text-sm font-semibold text-gray-500">
                            No products match this filter.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <h2 className="text-lg font-bold text-gray-950">Activity</h2>
                <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                  {bulkLog.length ? (
                    <div className="space-y-2 text-xs font-semibold text-gray-700">
                      {bulkLog.map((item, index) => (
                        <div key={`${item}-${index}`}>{item}</div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm font-semibold text-gray-500">
                      No bulk image activity yet.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Dialog
        open={!!responsePopup}
        onClose={() => setResponsePopup(null)}
        fullWidth
        maxWidth="md"
      >
        <div className="bg-white">
          <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
            <div className="min-w-0">
              <div className="text-base font-bold text-gray-950">
                API Response
              </div>
              <div className="mt-1 break-all font-mono text-xs font-semibold text-gray-500">
                {responsePopup?.fullUrl || ""}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setResponsePopup(null)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 transition hover:bg-gray-50"
              aria-label="Close API response"
            >
              <Close className="!text-[20px]" />
            </button>
          </div>
          <pre className="max-h-[70vh] overflow-auto bg-gray-950 p-4 text-xs leading-5 text-gray-50">
            {responsePopup ? JSON.stringify(responsePopup.data, null, 2) : ""}
          </pre>
        </div>
      </Dialog>
      <style>{`
        .condition-value-pulse {
          animation: condition-value-pulse 1.15s ease-in-out infinite;
        }

        @keyframes condition-value-pulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(245, 158, 11, .26);
          }
          50% {
            box-shadow: 0 0 0 5px rgba(245, 158, 11, .12);
          }
        }
      `}</style>
    </div>
  );
};

export default TechAdmin;
